import logo from "assets/logo.svg";
import { useEffect, useState, useContext } from "react";
import { ModalContext } from "hooks/ModalProvider";
import { FiArrowLeft, FiMenu, FiSettings, FiHelpCircle } from "react-icons/fi";
import { DateTime, Info, LicenseAgreement, UserMenu } from "processhub-components-lib";
import CharacterMenu from "components/CharacterMenu/CharacterMenu";
import SideMenu from "../SideMenu";
import useScreen from "hooks/useScreen";
import classNames from "utilities/ClassNames";
import { ReactComponent as HealthsensorIcon } from "assets/healthsensorIcon.svg";
import { useMinifiedMenu } from "hooks/useMinifiedMenu";
import { ReactComponent as AiChatIcon } from "assets/aiChatIcon.svg";
import allHooks from "hooks";
import { profileConfig } from "../config/userMenuConfig";
import { navLinks } from "../config/navLinksConfig";
import { useAuth } from "react-oidc-context";
import { getExtractedPath } from "utilities/extractedPath";
import { getCurrentSolution } from "api/solutions";
import { motion } from "framer-motion";
import { getSlideProjectName } from "../config/animations";
import extractUUIDs from "../utils/extractUUIDs";
import HeaderMenu from "./HeaderMenu";
import { useAiChat } from "hooks/AiChatContext";
import useApp from "hooks/useApp";
import { useUnityActions } from "hooks/unityActions";
import { FiX } from "react-icons/fi";
import licenseAgreementConfig from "config/license/licenseAgreementConfig";
import { getOrganization } from "api/organizations";
import { useLocation } from "react-router-dom";

export default function Header({ setShowHealth }: any) {
  const { setModalType, showKeyGuide, setShowKeyGuide } = useContext(ModalContext);
  const [showSideMenu, setShowMenu] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [settingsShown, setSettingsShown] = useState(false);
  const [tutorialsShown, setTutorialsShown] = useState(false);
  const { isScaled } = useScreen();
  const auth = useAuth();
  const logout = async () => auth.signoutRedirect();
  const baseUrl = getExtractedPath();
  const { currOrg, curSol } = extractUUIDs(baseUrl);
  const { aiChatShown, setAiChatShown } = useAiChat();
  const { setUser } = useApp();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { setIsMovementDisabled } = useUnityActions();
  const [showCharacterHasChanged, setShowCharacterHasChanged] = useState(false);
  const is3DDashboard = location.pathname.endsWith("/3d-dashboard");
  const [showLicense, setShowLicense] = useState(false);
  const slideProjectName = getSlideProjectName();
  const [showLicenseDetails, setShowLicenseDetails] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const { pathname } = useLocation();
  const isProjectsPage = pathname.includes("/projects");
  const getOrganizationInfo = async () => {
    const response = await getOrganization(currOrg);
    if (response.status === 200) {
      return response.data.data.name;
    }
    return '';
  };


  const onLicenseAccept = () => {
    setShowLicense(false);
  };

  const getProjectInfo = async () => {
    const response = await getCurrentSolution(currOrg, curSol);
    if (response.status === 200) {
      return response.data.data.name;
    }
    return '';
  };
  const {
    setMinifiedMenuOpen
  } = useMinifiedMenu();

  const buttonsConfig = [
    {
      name: 'AI ChatBot',
      shown: aiChatShown,
      type: 'component',
      onClick: () => setAiChatShown((prev: any) => !prev),
      Icon: AiChatIcon,
      activeClass: "headerIconActive",
      inactiveClass: "headerIconInactive"
    },
    {
      name: 'Settings',
      shown: settingsShown,
      type: 'icon',
      onClick: () => {
        setModalType('settings');
        setMinifiedMenuOpen(false);
      },
      Icon: FiSettings,
      activeClass: "text-blue",
      inactiveClass: "hover:text-green"
    },
    {
      name: 'Help',
      shown: tutorialsShown,
      type: 'icon',
      onClick: () => {
        setModalType('tutorials');
        setMinifiedMenuOpen(false);
      },
      Icon: FiHelpCircle,
      activeClass: "text-blue",
      inactiveClass: "hover:text-blue"
    }
  ];

  useEffect(() => {
    const fetchProjectName = async () => {
      const userProjectName = await getProjectInfo();
      setProjectName(userProjectName);
      const userOrganizationName = await getOrganizationInfo();
      setOrganizationName(userOrganizationName);
    };

    fetchProjectName();
  }, []);

  const headerMenuButtonsConfig = [{
    title: 'Licence Agreement',
    action: () => setShowLicense(true)
  }];

  const onCharacterConfirm = (name: string) => {
    setShowCharacterHasChanged(true);
    setUser((prev: any) => ({
      ...prev,
      characterName: name
    }));
  };

  useEffect(() => {
    showSideMenu ? setIsMovementDisabled(1) : setIsMovementDisabled(0);
  }, [showSideMenu]);

  return (
    <div className="w-full bg-dark relative z-40">
      <div
        className={classNames(
          "lg:h-12 xl-2xl:h-14 2xl:h-16",
          "mx-auto flex items-center px-3"
        )}
      >
        <div className="relative z-50 flex items-center justify-center gap-5">
          <div
            onClick={() => setShowMenu(!showSideMenu)}
            className="cursor-pointer duration-300"
          >
            {!showSideMenu && <FiMenu size={"30"} className="lg:w-[80%] xl-2xl:w-[90%] 2xl:w-full" />}
            {showSideMenu && <FiArrowLeft size={"30"} className="lg:w-[80%] xl-2xl:w-[90%] 2xl:w-full" />}
          </div>
          <a
            href={baseUrl}
            {...(is3DDashboard
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            <img
              src={logo}
              className={classNames("lg:h-6 xl-2xl:h-7 2xl:h-8")}
              alt="logo"
            />
          </a>
          {/* Animated project name */}
          <motion.div
            initial="hidden"
            animate={showSideMenu ? "visible" : "hidden"}
            variants={slideProjectName}
            className="mt-1"
          >
            {organizationName && (
              <span className="text-md 2xl:text-xl flex">
                {organizationName}
              </span>
            )}
          </motion.div>
          <div className="text-md 2xl:text-xl flex mt-[6px]">|</div>
          <motion.div
            initial="hidden"
            animate={showSideMenu ? "visible" : "hidden"}
            variants={slideProjectName}
            className="mt-1"
          >
            {projectName && (
              <span className="text-md 2xl:text-xl flex">
                {projectName}
              </span>
            )}
          </motion.div>
        </div>
        <div className="flex flex-wrap justify-end grow align-middle items-center">
          <HeaderMenu buttonsConfig={buttonsConfig} />
          <div className="flex mr-4">
            <DateTime hooks={allHooks} />
            <div className="hover:text-green cursor-pointer w-max mr-6" onClick={() => setShowHealth(true)}>
              <HealthsensorIcon className={classNames(isScaled ? "h-[19px] w-[19px]" : "h-[23px] w-[23px] border-white rounded-full hover:text-green")} />
            </div>
            <Info type='saas' setShowKeyGuide={setShowKeyGuide} showKeyGuide={showKeyGuide} hooks={allHooks} />
          </div>
          <UserMenu profileConfig={profileConfig} headerMenuButtons={headerMenuButtonsConfig} hooks={allHooks} logout={logout} showProfileMenu={showProfileMenu} setShowProfileMenu={setShowProfileMenu} >
            <CharacterMenu onCharacterConfirm={onCharacterConfirm} />
          </UserMenu>
          {
            showLicense &&
            <div className="absolute left-0 top-0 w-screen h-screen bg-dark/35 flex items-center justify-center align-middle" onClick={() => setShowLicense(false)}>
              <div className="w-[75%] h-[87%] mt-14 z-50 flex items-center justify-center relative pt-2 bg-black">
                <LicenseAgreement hasAction={false} onAccept={onLicenseAccept} mode="full" licenseAgreementConfig={licenseAgreementConfig} />
                <button
                  className="cursor-pointer bg-blue rounded-md p-1 h-min absolute top-3 right-6"
                  onClick={onLicenseAccept}
                >
                  <FiX size={20} />
                </button>
                <div className="absolute top-3 left-6">
                  <div className="flex gap-2">
                    <button
                      className="cursor-pointer border border-blue rounded-md px-1 py-0.5 h-min flex items-center justify-center text-center gap-1"
                      onClick={onLicenseAccept}
                    >
                      <FiArrowLeft className="text-blue" />
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          }
          <SideMenu
            closeMenu={() => setShowMenu(false)}
            showSideMenu={showSideMenu}
            navLinks={navLinks}
            hooks={allHooks}
          />
        </div>
      </div>
    </div>
  );
}